import { Text } from '@televet/kibble-ui/Text';
import { Button } from '@televet/kibble-ui/Button';
import { NumberInput } from '@televet/kibble-ui/NumberInput';
import { Tag } from '@televet/kibble-ui/Tag';
import { Box, Flex, Table } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { TablePagination } from '@/components/TablePagination';
import { trpc, trpcClient } from '@/lib/trpc';
import { intentStatusVariantMap } from '@/features/workflows/badgePalettes';
import { MailroomIntentActions } from '@/features/workflows/components/MailroomIntentActions';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  calendarDateToIsoEnd,
  calendarDateToIsoStart,
} from '@/features/listing-filters/calendarDate';
import { MailroomFilters } from './-components/MailroomFilters';
import {
  mailroomFiltersSchema,
  mailroomSearchSchema,
  type MailroomFilterValues,
} from './-lib/filters';

const RouteComponent = () => {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const { page = 1, ...filters } = Route.useSearch();
  const [confirmTrigger, setConfirmTrigger] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [lookbackHours, setLookbackHours] = useState(24);
  const triggeredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (triggeredTimerRef.current) clearTimeout(triggeredTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const applyFilters = useCallback(
    (next: MailroomFilterValues) => {
      // Any filter change invalidates the current page number.
      navigate({ search: { ...next, page: undefined } });
    },
    [navigate]
  );

  const [searchDraft, setSearchDraft] = useDebouncedFilterText({
    value: filters.search,
    onCommit: (next) =>
      navigate({
        replace: true,
        search: (prev) => ({ ...prev, search: next, page: undefined }),
      }),
  });

  useListingFilters({
    page: 'workflow-mailroom',
    schema: mailroomFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const where = useMemo(
    () => ({
      ...(filters.status?.length && { status: filters.status }),
      ...(filters.channel?.length && { channel: filters.channel }),
      ...(filters.recipientType?.length && { recipientType: filters.recipientType }),
      ...(filters.search && { search: filters.search }),
      ...(filters.hasError !== undefined && { hasError: filters.hasError }),
      ...(filters.hasSuppression !== undefined && { hasSuppression: filters.hasSuppression }),
      scheduledFrom: calendarDateToIsoStart(filters.scheduledFrom),
      scheduledTo: calendarDateToIsoEnd(filters.scheduledTo),
    }),
    [filters]
  );

  const { data, isLoading } = useQuery(
    trpc.workflow.listMailroomIntents.queryOptions({ page, limit: 25, where })
  );

  const triggerMutation = useMutation({
    mutationFn: () => trpcClient.workflow.triggerMailroom.mutate({ lookbackHours }),
    onSuccess: () => {
      setConfirmTrigger(false);
      setTriggered(true);
      setTriggerError(null);
      queryClient.invalidateQueries({
        queryKey: trpc.workflow.listMailroomIntents.queryKey(),
      });
      triggeredTimerRef.current = setTimeout(() => setTriggered(false), 3000);
    },
    onError: (error) => {
      setConfirmTrigger(false);
      setTriggerError(error instanceof Error ? error.message : 'Failed to trigger mailroom');
      errorTimerRef.current = setTimeout(() => setTriggerError(null), 5000);
    },
  });

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs items={[{ label: 'Mailroom' }]} />

        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          {/* Same heading style as every other listing page in this section and
              the emitter one, rather than the larger bold/xl it used before. */}
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Mailroom
          </Text>
          <Flex align="center" gap={2}>
            {triggered && (
              <Text fontSize="sm" variant="success">
                Queued
              </Text>
            )}
            {triggerError && (
              <Text fontSize="sm" variant="danger">
                {triggerError}
              </Text>
            )}
            {confirmTrigger ? (
              <Flex align="center" gap={2}>
                <NumberInput
                  size="sm"
                  w="80px"
                  min={1}
                  value={String(lookbackHours)}
                  onValueChange={(details) => {
                    const val = parseInt(details.value, 10);
                    if (!isNaN(val) && val > 0) setLookbackHours(val);
                  }}
                />
                <Text fontSize="xs" variant="subtle">
                  hrs lookback
                </Text>
                <Button variant="ghost" size="sm" onClick={() => setConfirmTrigger(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  loading={triggerMutation.isPending}
                  onClick={() => triggerMutation.mutate()}
                >
                  Confirm
                </Button>
              </Flex>
            ) : (
              <Button
                variant="primary"
                size="sm"
                iconName="magic"
                iconPosition="left"
                onClick={() => setConfirmTrigger(true)}
              >
                Trigger Mailroom
              </Button>
            )}
          </Flex>
        </Flex>

        <MailroomFilters
          values={filters}
          onChange={applyFilters}
          searchDraft={searchDraft}
          onSearchDraftChange={setSearchDraft}
        />

        {/* The table scrolls inside its own box, so a wide row never widens the page. */}
        <Box overflowX="auto" width="100%">
          {/* Not `interactive`: these rows have no click target, so a hover
              highlight would suggest one. */}
          <Table.Root size="sm" width="100%" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Recipient ID</Table.ColumnHeader>
                <Table.ColumnHeader>Recipient Type</Table.ColumnHeader>
                <Table.ColumnHeader>Channel</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Scheduled</Table.ColumnHeader>
                <Table.ColumnHeader>Sent</Table.ColumnHeader>
                <Table.ColumnHeader>Error / Suppression</Table.ColumnHeader>
                <Table.ColumnHeader>Workflow</Table.ColumnHeader>
                <Table.ColumnHeader>Run</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.intents.map((intent) => (
                <Table.Row key={intent.id}>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="mono">
                      {intent.recipientId.slice(0, 12)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Tag label={intent.recipientType} variant="default" size="sm" />
                  </Table.Cell>
                  <Table.Cell>
                    <Tag label={intent.channel} variant="default" size="sm" />
                  </Table.Cell>
                  <Table.Cell>
                    <Tag
                      label={intent.status}
                      variant={intentStatusVariantMap[intent.status] ?? 'default'}
                      size="sm"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs">{new Date(intent.scheduledFor).toLocaleString()}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs">
                      {intent.sentAt ? new Date(intent.sentAt).toLocaleString() : '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell maxW="200px">
                    <Text
                      fontSize="xs"
                      color="text.subtle"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={intent.errorDetail ?? intent.suppressionReason ?? undefined}
                    >
                      {intent.errorDetail ?? intent.suppressionReason ?? '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell maxW="200px">
                    {/* The workflow that produced this intent. Two hops from the
                      intent, which hangs off a run task rather than the run. */}
                    {/* CSS ellipsis, not Kibble's `truncated`: that measures
                      then sets state, which visibly reflows a table column
                      after paint. */}
                    <Text
                      fontSize="xs"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={intent.workflowRunTask.workflowRun?.workflow?.name ?? undefined}
                    >
                      {intent.workflowRunTask.workflowRun?.workflow?.name ?? '—'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Link
                      to="/workflow-runs/$runId"
                      params={{ runId: intent.workflowRunTask.workflowRunId }}
                      style={{ textDecoration: 'none' }}
                    >
                      <Text fontSize="xs" fontFamily="mono" variant="accent">
                        {intent.workflowRunTask.workflowRunId.slice(0, 8)}
                      </Text>
                    </Link>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <MailroomIntentActions intent={{ id: intent.id, status: intent.status }} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {!isLoading && data?.intents.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            <Text variant="subtle">No intents match the current filter</Text>
          </Flex>
        )}

        {data?.pagination && (
          <TablePagination
            page={data.pagination.page}
            totalPages={data.pagination.totalPages}
            hasPrev={data.pagination.hasPrev}
            hasNext={data.pagination.hasNext}
            summary={`${data.intents.length} intents on this page`}
            onPageChange={(nextPage) =>
              navigate({
                search: (prev) => ({ ...prev, page: nextPage > 1 ? nextPage : undefined }),
              })
            }
          />
        )}
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflow-mailroom/')({
  component: RouteComponent,
  validateSearch: mailroomSearchSchema,
});
